import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await authAPI.me();
      setUser(res.data);
    } catch {
      localStorage.removeItem('vx_session_token');
      localStorage.removeItem('vx_user');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const testLogin = async (email, password) => {
    const res = await authAPI.testLogin({ email, password });
    const { user: userData, session_token } = res.data;
    localStorage.setItem('vx_session_token', session_token);
    localStorage.setItem('vx_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const localLogin = async (email, password) => {
    const res = await authAPI.login({ email, password });
    const { user: userData, session_token } = res.data;
    localStorage.setItem('vx_session_token', session_token);
    localStorage.setItem('vx_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const setUserData = (userData, sessionToken) => {
    if (sessionToken) localStorage.setItem('vx_session_token', sessionToken);
    localStorage.setItem('vx_user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = async () => {
    try { await authAPI.logout(); } catch (e) { console.error(e); }
    localStorage.removeItem('vx_session_token');
    localStorage.removeItem('vx_user');
    setUser(null);
  };

  const isSuperUser = user?.role === 'super_user';
  const isManager = user?.role === 'manager';
  const isStaff = user?.role === 'staff';
  const isAdmin = isSuperUser || isManager;
  const canAccessLeads = !!user && (
    isSuperUser || isManager ||
    user.staff_role === 'sales' ||
    (user.permissions || []).includes('lead_management')
  );

  return (
    <AuthContext.Provider value={{
      user, loading, loginWithGoogle, testLogin, localLogin, logout,
      isSuperUser, isManager, isStaff, isAdmin, canAccessLeads, refreshUser: checkAuth, setUserData
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
